import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserFormDialog } from "@/components/UserFormDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABEL, ROLE_TONE } from "@/lib/roles";

const UserRow = ({ u, onEdit, onToggle, onDelete }) => {
  const isOwner = u.role === "owner";
  return (
    <TableRow data-testid={`user-row-${u.id}`} className={u.active ? "" : "opacity-60"}>
      <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
      <TableCell className="text-slate-600">{u.email}</TableCell>
      <TableCell>
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${ROLE_TONE[u.role] || "bg-slate-100 text-slate-600"}`} data-testid={`user-role-${u.id}`}>
          {isOwner && <ShieldCheck className="h-3 w-3" />}{ROLE_LABEL[u.role] || u.role}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch checked={u.active} disabled={isOwner} onCheckedChange={(v) => onToggle(u, v)} data-testid={`user-active-switch-${u.id}`} />
          <span className="text-xs text-slate-500" data-testid={`user-status-${u.id}`}>{u.active ? "Aktif" : "Nonaktif"}</span>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <Button size="icon" variant="ghost" onClick={() => onEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>
        {!isOwner && (
          <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => onDelete(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export default function KelolaUser() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const isOwner = user?.role === "owner";

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  if (!isOwner) return <p className="text-slate-500" data-testid="users-forbidden">Halaman ini hanya untuk pemilik toko.</p>;

  const openForm = (u = null) => { setEditing(u); setFormOpen(true); };
  const toggle = async (u, active) => {
    try {
      await api.put(`/users/${u.id}`, { name: u.name, email: u.email, active });
      toast.success(active ? "User diaktifkan" : "User dinonaktifkan");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleting.id}`);
      toast.success("User dihapus");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setDeleting(null);
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">Kelola User</h1>
          <p className="mt-1 text-sm text-slate-500">Admin bisa memakai semua menu kecuali Kelola User. Kasir hanya bisa membuka Kasir, Riwayat, dan Menu Pelanggan.</p>
        </div>
        <Button onClick={() => openForm()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-user-button">
          <Plus className="mr-2 h-4 w-4" /> Tambah User
        </Button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Peran</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => <UserRow key={u.id} u={u} onEdit={openForm} onToggle={toggle} onDelete={setDeleting} />)}
          </TableBody>
        </Table>
      </div>

      <UserFormDialog open={formOpen} user={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus user?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.name}" tidak akan bisa login lagi. Riwayat transaksi tetap tersimpan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="user-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
